import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Clock, Calendar, Crown, AlertCircle, MapPin, ChevronDown, Home } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const ThisWeek = ({ reminders = [], plannedActions = [], queenBatches = [], onMarkDone, onSnooze, onActionDone, onActionSnooze }) => {

  // Extract a clean task title and assignee from planned actions
  const getActionDisplay = (action) => {
    let title = '';
    let assignee = action.assignedBeekeeper || action.assigned_to_name || '';
    const desc = action.description || '';

    // Prefer the title field (crew tasks have it)
    if (action.title) {
      title = action.title.replace(/^Crew Task:\s*/i, '');
    }

    // Parse description — strip "Assigned to: Name\n" prefix if present
    if (!title && desc) {
      const lines = desc.split('\n').map(l => l.trim()).filter(Boolean);
      if (lines[0]?.startsWith('Assigned to:')) {
        if (!assignee) assignee = lines[0].replace('Assigned to:', '').trim();
        title = lines.slice(1).join(' ').trim();
      } else {
        title = lines.join(' ');
      }
    }

    return { title: title || 'Scheduled Task', assignee };
  };
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('reminders');
  const [showSnoozeDialog, setShowSnoozeDialog] = useState(false);
  const [selectedReminder, setSelectedReminder] = useState(null);
  const [snoozeDate, setSnoozeDate] = useState('');
  const [completedIds, setCompletedIds] = useState(new Set());
  const [snoozedIds, setSnoozedIds] = useState(new Set());
  const [snoozeType, setSnoozeType] = useState('reminder'); // 'reminder' or 'action'
  const [yardFilter, setYardFilter] = useState('all');

  const handleDone = (reminder) => {
    // For queen rearing reminders, just mark as done locally (no hive to record on)
    if (reminder.type === 'queen_rearing') {
      setCompletedIds(prev => new Set([...prev, reminder.id]));
      return;
    }
    
    const now = new Date();
    const doneRecord = {
      action: reminder.action,
      completedAt: now.toISOString(),
      completedDate: now.toLocaleDateString(),
      completedTime: now.toLocaleTimeString(),
      hiveId: reminder.hiveId,
      hiveNumber: reminder.hiveNumber
    };
    // Mark as completed locally to remove from list immediately
    setCompletedIds(prev => new Set([...prev, reminder.id || `${reminder.hiveId}-${reminder.action}`]));
    onMarkDone?.(reminder, doneRecord);
  };

  const handleSnoozeClick = (reminder, type = 'reminder') => {
    setSelectedReminder(reminder);
    setSnoozeType(type);
    // Set default to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setSnoozeDate(formatDateLocal(tomorrow));
    setShowSnoozeDialog(true);
  };

  // Helper to format date as YYYY-MM-DD in local timezone
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const handleSnoozeConfirm = () => {
    if (selectedReminder && snoozeDate) {
      if (snoozeType === 'action') {
        // For planned actions, call the action snooze handler
        onActionSnooze?.(selectedReminder, snoozeDate);
      } else {
        // For reminders, mark as snoozed locally
        setSnoozedIds(prev => new Set([...prev, selectedReminder.id || `${selectedReminder.hiveId}-${selectedReminder.action}`]));
        onSnooze?.(selectedReminder, snoozeDate);
      }
      setShowSnoozeDialog(false);
      setSelectedReminder(null);
    }
  };

  const handleActionDone = (action) => {
    setCompletedIds(prev => new Set([...prev, action.id]));
    onActionDone?.(action);
  };

  // Filter out completed and snoozed reminders
  const visibleReminders = reminders.filter(r => {
    const reminderId = r.id || `${r.hiveId}-${r.action}`;
    return !completedIds.has(reminderId) && !snoozedIds.has(reminderId);
  });
  
  // Filter planned actions - only show pending ones not completed locally
  const visibleActions = plannedActions.filter(a => 
    a.status !== 'completed' && !completedIds.has(a.id)
  );
  
  // Separate actions that need dates vs ones that have dates
  const actionsNeedingDates = visibleActions.filter(a => !a.date || a.date === '');
  const scheduledActions = visibleActions.filter(a => a.date && a.date !== '');
  
  // Extract unique yards from ALL sources (actions + reminders) for global filter
  const availableYards = useMemo(() => {
    const yards = new Set();
    const addYard = (y) => {
      if (y && y !== 'None' && y !== 'NONE' && y.trim()) {
        y.split(',').forEach(part => { const trimmed = part.trim(); if (trimmed) yards.add(trimmed); });
      }
    };
    visibleActions.forEach(a => addYard(a.yard));
    visibleReminders.forEach(r => addYard(r.yard));
    return Array.from(yards).sort();
  }, [visibleActions, visibleReminders]);
  
  // Filter scheduled actions by yard
  const filteredScheduled = useMemo(() => {
    if (yardFilter === 'all') return scheduledActions;
    return scheduledActions.filter(a => a.yard && a.yard.split(',').map(y => y.trim()).includes(yardFilter));
  }, [scheduledActions, yardFilter]);
  
  const filteredNeedingDates = useMemo(() => {
    if (yardFilter === 'all') return actionsNeedingDates;
    return actionsNeedingDates.filter(a => a.yard && a.yard.split(',').map(y => y.trim()).includes(yardFilter));
  }, [actionsNeedingDates, yardFilter]);
  
  // Separate hive reminders from queen rearing reminders
  const hiveReminders = visibleReminders.filter(r => r.type !== 'queen_rearing');
  const queenReminders = visibleReminders.filter(r => r.type === 'queen_rearing');

  // Filter reminders by yard
  const filteredHiveReminders = useMemo(() => {
    if (yardFilter === 'all') return hiveReminders;
    return hiveReminders.filter(r => r.yard && r.yard.split(',').map(y => y.trim()).includes(yardFilter));
  }, [hiveReminders, yardFilter]);

  const filteredQueenReminders = useMemo(() => {
    if (yardFilter === 'all') return queenReminders;
    // Queen reminders may not have yard, show all if no yard match
    return queenReminders.filter(r => !r.yard || r.yard.split(',').map(y => y.trim()).includes(yardFilter));
  }, [queenReminders, yardFilter]);

  return (
    <div className="bg-white rounded-xl border border-stone-200 shadow-sm" data-testid="this-week">
      <div className="p-4 border-b border-stone-100">
        <div className="flex items-center justify-between">
          <h2 className="font-bold text-black text-lg">Tasks & Reminders</h2>
          {/* Global Apiary Filter */}
          {availableYards.length > 1 && (
            <div className="flex items-center gap-1.5" data-testid="yard-filter-global">
              <MapPin className="h-3.5 w-3.5 text-amber-600" />
              <div className="relative">
                <select
                  value={yardFilter}
                  onChange={(e) => setYardFilter(e.target.value)}
                  className="h-8 rounded-lg border border-stone-200 bg-stone-50 pl-2 pr-7 text-xs appearance-none cursor-pointer focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                  data-testid="yard-filter-select"
                >
                  <option value="all">All Apiaries</option>
                  {availableYards.map(y => (
                    <option key={y} value={y}>{y}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-stone-400 pointer-events-none" />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-stone-100">
        <button
          onClick={() => setActiveTab('reminders')}
          className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
            activeTab === 'reminders' 
              ? 'text-amber-700 border-b-2 border-amber-500 bg-amber-50' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <div className="flex items-center justify-center gap-1">
            <Check className="h-4 w-4" />
            <span className="hidden sm:inline">Hive</span> Reminders ({filteredHiveReminders.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('queen')}
          className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
            activeTab === 'queen' 
              ? 'text-pink-700 border-b-2 border-pink-500 bg-pink-50' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <div className="flex items-center justify-center gap-1">
            <Crown className="h-4 w-4" />
            Queen ({filteredQueenReminders.length})
          </div>
        </button>
        <button
          onClick={() => setActiveTab('scheduled')}
          className={`flex-1 px-3 py-3 text-sm font-medium transition-colors ${
            activeTab === 'scheduled' 
              ? 'text-amber-700 border-b-2 border-amber-500 bg-amber-50' 
              : 'text-stone-500 hover:text-stone-700'
          }`}
        >
          <div className="flex items-center justify-center gap-1">
            <Clock className="h-4 w-4" />
            <span className="hidden sm:inline">Scheduled</span> ({filteredScheduled.length})
            {filteredNeedingDates.length > 0 && (
              <span className="bg-orange-500 text-white text-xs px-1.5 py-0.5 rounded-full">
                {filteredNeedingDates.length}
              </span>
            )}
          </div>
        </button>
      </div>

      {/* Content */}
      <div className="p-4 space-y-3 max-h-[400px] overflow-y-auto">
        {/* Hive Reminders Tab */}
        {activeTab === 'reminders' && (
          <>
            {filteredHiveReminders.length === 0 ? (
              <div className="text-center py-6 text-stone-500">
                <Check className="h-8 w-8 mx-auto mb-2 text-green-500" />
                <p>{yardFilter !== 'all' ? `No reminders for ${yardFilter}` : 'No hive reminders'}</p>
                {yardFilter !== 'all' && (
                  <button onClick={() => setYardFilter('all')} className="text-amber-600 text-sm mt-1 hover:underline">Show all apiaries</button>
                )}
              </div>
            ) : (
              filteredHiveReminders.map((reminder, idx) => (
                <div 
                  key={idx}
                  className="bg-stone-50 border border-stone-200 rounded-lg p-3 hover:bg-stone-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate(`/?view=hive-detail&hiveId=${reminder.hiveId}`)}
                    >
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-black">Hive {reminder.hiveNumber}</span>
                        <span className="text-stone-500 text-sm">{reminder.yard}</span>
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <span className="text-amber-600">●</span>
                        <span className="text-sm text-stone-600">{reminder.action}</span>
                      </div>
                      {reminder.date && (
                        <div className="text-xs text-stone-400 mt-1">
                          {new Date(reminder.date).toLocaleDateString()}
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 ml-3">
                      <Button
                        size="sm"
                        onClick={() => handleDone(reminder)}
                        className="h-8 px-3 text-xs font-semibold bg-green-500 hover:bg-green-600 text-white"
                        title="Mark as Done"
                      >
                        ✓ Done
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleSnoozeClick(reminder, 'reminder')}
                        className="h-8 px-3 text-xs font-semibold border-stone-300 text-black"
                        title="Snooze - reschedule for later"
                      >
                        ⏰
                      </Button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* Queen Rearing Tab */}
        {activeTab === 'queen' && (
          <>
            {filteredQueenReminders.length === 0 ? (
              <div className="text-center py-6 text-stone-500">
                <Crown className="h-8 w-8 mx-auto mb-2 text-pink-300" />
                <p>{yardFilter !== 'all' ? `No queen tasks for ${yardFilter}` : 'No queen rearing tasks'}</p>
                {yardFilter !== 'all' ? (
                  <button onClick={() => setYardFilter('all')} className="text-amber-600 text-sm mt-1 hover:underline">Show all apiaries</button>
                ) : (
                  <p className="text-xs mt-1">Queen tasks appear automatically when a batch has a graft date</p>
                )}
              </div>
            ) : (
              filteredQueenReminders.map((reminder, idx) => (
                <div 
                  key={`queen-${idx}`}
                  className="bg-pink-50 border border-pink-200 rounded-lg p-3 hover:bg-pink-100 transition-colors"
                >
                  <div className="flex items-start justify-between">
                    <div 
                      className="flex-1 cursor-pointer"
                      onClick={() => navigate('/queen-rearing?tab=batches')}
                    >
                      <div className="flex items-center gap-2">
                        <Crown className="h-4 w-4 text-pink-600" />
                        <span className="font-semibold text-pink-800">{reminder.batchName}</span>
                      </div>
                      <div className="text-sm text-pink-700 mt-1">{reminder.action}</div>
                      <div className="text-xs text-pink-500 mt-1">
                        {new Date(reminder.date).toLocaleDateString()}
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleDone(reminder)}
                      className="h-8 px-3 text-xs font-semibold bg-pink-500 hover:bg-pink-600 text-white"
                      title="Mark as Done"
                    >
                      ✓ Done
                    </Button>
                  </div>
                </div>
              ))
            )}
          </>
        )}

        {/* Scheduled Actions Tab */}
        {activeTab === 'scheduled' && (
          <>

            {/* Actions needing dates - show first with alert */}
            {filteredNeedingDates.length > 0 && (
              <div className="mb-4">
                <div className="flex items-center gap-2 mb-2 px-1">
                  <AlertCircle className="h-4 w-4 text-orange-500" />
                  <span className="text-sm font-semibold text-orange-700">Needs scheduling</span>
                </div>
                {filteredNeedingDates.map((action) => {
                  const { title: taskTitle, assignee } = getActionDisplay(action);
                  return (
                  <div 
                    key={action.id}
                    className="bg-orange-50 border border-orange-200 rounded-lg p-3 mb-2"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="font-semibold text-black">{taskTitle}</div>
                        {assignee && (
                          <div className="text-xs text-stone-500 mt-1">
                            Assigned to: {assignee}
                          </div>
                        )}
                        {(action.hive_number || action.yard) && (
                          <div className="flex items-center gap-1.5 text-xs mt-1">
                            {action.hive_number && action.hive_number !== 'None' && action.hive_number !== '0' && (
                              <span className="flex items-center gap-1 text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                                <Home className="h-3 w-3" />
                                {String(action.hive_number).startsWith('#') ? action.hive_number : `Hive ${action.hive_number}`}
                              </span>
                            )}
                            {action.yard && action.yard !== 'None' && action.yard !== 'NONE' && (
                              <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                                <MapPin className="h-3 w-3" />
                                {action.yard}
                              </span>
                            )}
                          </div>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleSnoozeClick(action, 'action')}
                        className="h-8 px-3 text-xs font-semibold bg-orange-500 hover:bg-orange-600 text-white"
                      >
                        Set Date
                      </Button>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
            
            {/* Scheduled actions with dates */}
            {filteredScheduled.map((action) => {
              const { title: taskTitle, assignee } = getActionDisplay(action);
              return (
              <div 
                key={action.id}
                className="bg-stone-50 border border-stone-200 rounded-lg p-3 mb-2 hover:bg-stone-100 transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="font-semibold text-black">{taskTitle}</div>
                    <div className="flex items-center gap-2 mt-1">
                      <Calendar className="h-3 w-3 text-amber-600" />
                      <span className="text-sm text-amber-700">
                        {new Date(action.date + 'T00:00:00').toLocaleDateString('en-US', { 
                          weekday: 'short', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                    </div>
                    {assignee && (
                      <div className="text-xs text-stone-500 mt-1">
                        Assigned to: {assignee}
                      </div>
                    )}
                    {(action.hive_number || action.yard) && (
                      <div className="flex items-center gap-1.5 text-xs mt-1 flex-wrap">
                        {action.hive_number && action.hive_number !== 'None' && action.hive_number !== '0' && (
                          <span className="flex items-center gap-1 text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">
                            <Home className="h-3 w-3" />
                            {String(action.hive_number).startsWith('#') ? action.hive_number : `Hive ${action.hive_number}`}
                          </span>
                        )}
                        {action.yard && action.yard !== 'None' && action.yard !== 'NONE' && (
                          <span className="flex items-center gap-1 text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded">
                            <MapPin className="h-3 w-3" />
                            {action.yard}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex gap-2 ml-3">
                    <Button
                      size="sm"
                      onClick={() => handleActionDone(action)}
                      className="h-8 px-3 text-xs font-semibold bg-green-500 hover:bg-green-600 text-white"
                    >
                      Done
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleSnoozeClick(action, 'action')}
                      className="h-8 px-3 text-xs font-semibold border-stone-300 text-black"
                      title="Reschedule to a different date"
                    >
                      Snooze
                    </Button>
                  </div>
                </div>
              </div>
            );
            })}
            
            {filteredScheduled.length === 0 && filteredNeedingDates.length === 0 && (
              <div className="text-center py-8">
                <div className="text-4xl mb-2">📅</div>
                <p className="text-stone-500">
                  {yardFilter !== 'all' ? `No tasks for ${yardFilter}` : 'No scheduled actions'}
                </p>
                {yardFilter !== 'all' && (
                  <button onClick={() => setYardFilter('all')} className="text-amber-600 text-sm mt-1 hover:underline">
                    Show all apiaries
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {/* Snooze Dialog */}
      <Dialog open={showSnoozeDialog} onOpenChange={setShowSnoozeDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {snoozeType === 'action' ? 'Schedule Action' : 'Snooze Reminder'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            {selectedReminder && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                {snoozeType === 'action' ? (
                  <>
                    <p className="font-semibold text-black">{selectedReminder.description}</p>
                    {selectedReminder.hive_number && (
                      <p className="text-sm text-stone-600">Hive {selectedReminder.hive_number}</p>
                    )}
                    {selectedReminder.assignedBeekeeper && (
                      <p className="text-sm text-stone-600">Assigned to: {selectedReminder.assignedBeekeeper}</p>
                    )}
                  </>
                ) : (
                  <>
                    <p className="font-semibold text-black">
                      {selectedReminder.hiveNumber ? `Hive ${selectedReminder.hiveNumber}` : selectedReminder.batchName || 'Task'}
                    </p>
                    <p className="text-sm text-stone-600">{selectedReminder.action}</p>
                  </>
                )}
              </div>
            )}
            <div>
              <label className="block text-sm font-medium text-black mb-2">
                <Calendar className="h-4 w-4 inline mr-2" />
                {snoozeType === 'action' ? 'Schedule for:' : 'Reschedule to:'}
              </label>
              <input
                type="date"
                value={snoozeDate}
                onChange={(e) => setSnoozeDate(e.target.value)}
                min={formatDateLocal(new Date())}
                className="w-full px-3 py-2 border border-stone-300 rounded-lg text-black focus:ring-2 focus:ring-amber-400 focus:outline-none"
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setShowSnoozeDialog(false)}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSnoozeConfirm}
                className="bg-amber-500 hover:bg-amber-600 text-black"
              >
                Confirm Snooze
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ThisWeek;
